<template>
  <!--
    O primeiro peixe do dia, em papel (D0, D15). A versão antiga era um modal azul-arroxeado com
    troféu-emoji e 50 confetes — fora da língua do jogo. A celebração fica com o SOM (firstFishDay,
    que continua tocando) e com o latão da pérola: uma vez por dia, sem fogos.
  -->
  <transition name="fade">
    <div v-if="show" class="primeiro-peixe" @click.self="close">
      <div class="primeiro-peixe__papel subir-surgindo">
        <FishIcon nome="trophy" tamanho="enorme" class="primeiro-peixe__trofeu" />

        <h2 class="primeiro-peixe__titulo">{{ $t('fishing.firstFish.title') }}</h2>
        <p class="primeiro-peixe__texto">{{ $t('fishing.firstFish.message') }}</p>

        <div v-if="fish" class="primeiro-peixe__peixe">
          <img :src="getFishImage(fish.name)" :alt="$t(`fishes.${fish.name}`)" />
          <div>
            <p class="primeiro-peixe__nome">{{ $t(`fishes.${fish.name}`) }}</p>
            <p class="primeiro-peixe__raridade">{{ $t(`fishes.${fish.rarity}`) }}</p>
          </div>
        </div>

        <!--
          O número vem do servidor (`bonusPrimeiroPeixe` na captura): é o que foi
          de fato creditado, e não uma conta refeita aqui que poderia divergir.
          Sem o valor (captura antiga na tela), cai na fórmula do jogo.
        -->
        <p class="primeiro-peixe__bonus">
          {{ $t('fishing.firstFish.bonusXP') }}
          <strong class="t-numero">{{ bonus || Math.floor(50 * (playerLevel * 5)) }}</strong>
        </p>

        <FishButton variante="primario" bloco som="recompensa" @click="close">
          {{ $t('fishing.firstFish.continue') }}
        </FishButton>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { getFishImage } from '@/shared/utils/fishesImages';
import { FishButton, FishIcon } from '@/shared/ui';

export default {
  name: 'FirstFishCelebration',
  components: { FishButton, FishIcon },
  computed: {
    ...mapState('player', {
      playerLevel: state => state.level
    }),
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    fish: {
      type: Object,
      default: null
    },
    /** O bônus de XP creditado pelo servidor nesta captura. */
    bonus: {
      type: Number,
      default: 0
    },
    playerId: {
      type: [String, Number],
      default: null
    }
  },
  emits: ['close'],
  methods: {
    ...mapActions('SoundController', ['playSound']),

    getFishImage(name) {
      return getFishImage(name);
    },

    close() {
      // O som do clique fica com o FishButton; aqui é só devolver a tela.
      this.$emit('close');
    },

    async playCelebrationSound() {
      await this.playSound({ category: 'fishing', type: 'firstFishDay' });
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.playCelebrationSound();
      }
    }
  }
}
</script>

<style scoped>
.primeiro-peixe {
  position: fixed;
  inset: 0;
  z-index: 60; /* acima do CatchResult (50): os dois abrem juntos no primeiro peixe do dia */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--e-3);
  background: rgba(10, 20, 28, 0.65);
}

.primeiro-peixe__papel {
  width: min(400px, 100%);
  border: var(--moldura-borda) solid transparent;
  border-image: url('/ui/frame-panel.png') var(--moldura-fatia) / var(--moldura-borda) repeat;
  background: var(--papel);
  background-clip: padding-box;
  image-rendering: pixelated;
  padding: var(--e-4);
  text-align: center;
  color: var(--tinta);
  position: relative;
  box-shadow: 0 8px 0 rgba(0, 0, 0, 0.4);
}

/* O fio de latão é um ::after: o `fill` do pergaminho pinta o miolo por cima de borda e sombra
 * interna — só a camada de filho sobrevive às duas peles (mesma decisão do CatchResult). */
.primeiro-peixe__papel::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 6px;
  background: var(--latao);
}

/* No desktop a celebração é o PERGAMINHO, como todo modal (pedido do autor, 03/09). */
@media (min-width: 600px) {
  .primeiro-peixe__papel {
    width: min(460px, 100%);
    border-style: solid;
    border-color: transparent;
    border-width: 12px 24px;
    border-image: url('/ui/pergaminho.png') 12 24 fill / 12px 24px round;
    background: none;
    box-shadow: none;
  }

  .primeiro-peixe__papel::after {
    left: var(--e-2);
    right: var(--e-2);
  }
}

.primeiro-peixe__trofeu {
  margin-bottom: var(--e-2);
}

.primeiro-peixe__titulo {
  margin: 0 0 var(--e-2);
  font-family: var(--fonte-display);
  font-size: var(--f-4);
  font-weight: 400;
  color: var(--tinta);
}

.primeiro-peixe__texto {
  margin: 0 0 var(--e-3);
  font-size: var(--f-2);
  color: var(--tinta-fraca);
}

.primeiro-peixe__peixe {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--e-3);
  padding: var(--e-2) 0;
  border-top: 2px dashed var(--papel-sombra);
  border-bottom: 2px dashed var(--papel-sombra);
  margin-bottom: var(--e-3);
}

.primeiro-peixe__peixe img {
  width: 96px;
  height: 72px;
  object-fit: contain;
  image-rendering: auto;
}

.primeiro-peixe__nome {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-3);
}

.primeiro-peixe__raridade {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.primeiro-peixe__bonus {
  margin: 0 0 var(--e-3);
  font-size: var(--f-2);
  color: var(--tinta-fraca);
}

.primeiro-peixe__bonus strong {
  color: var(--latao);
  font-size: var(--f-4);
  margin-left: var(--e-1);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity var(--t-medio) var(--curva-suave);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
